<template>
  <div class="container">
    <!-- Filters -->
    <div class="filter-container">
      <div class="filter-item dealer-name-item">
        <label for="dealerSearch">Search</label>
        <input
          id="dealerSearch"
          type="text"
          v-model="dealerSearch"
          class="search-input"
          placeholder="Search by business name, owner or mobile..."
        />
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Data Table -->
    <div v-if="!loading && dealers.length > 0" class="table-container">
      <table>
        <thead>
          <tr>
            <th>Business Name</th>
            <th>Owner</th>
            <th>Mobile</th>
            <th>Distributor</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="dealer in filteredDealers" :key="dealer.id">
            <td>{{ dealer.businessName }}</td>
            <td>{{ dealer.ownerName }}</td>
            <td>{{ dealer.mobile }}</td>
            <td>{{ dealer.distributorName }}</td>
            <td>{{ dealer.createdBy }}</td>
            <td>{{ formatDateTime(dealer.createdAt) }}</td>
            <td class="dealer-actions">
              <button
                type="button"
                class="icon-button"
                title="Review"
                @click="reviewDealer(dealer.id)"
              >
                <i class="fa fa-eye"></i>
              </button>
            </td>
          </tr>
          <tr v-if="filteredDealers.length === 0">
            <td colspan="7" style="text-align: center; color: gray;">No dealers found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- No Data Found -->
    <div v-if="!loading && dealers.length === 0" class="not-found-widget">
      <img src="@/assets/vectors/data_not_found.png" alt="No Dealers" class="error-image" />
      <p class="text-center error-message">No Pending Dealers!</p>
    </div>
  </div>
</template>

<script>
import { fetchTempDealerList } from "@/services/dataService";

export default {
  name: 'DealerApprovalList',

  data() {
    return {
      token: localStorage.getItem('token_set'),
      profileId: localStorage.getItem('profile_id'),
      dealerSearch: "",
      dealers: [],
      loading: false,
      hasLoaded: false,
    };
  },

  computed: {
    filteredDealers() {
      const search = this.dealerSearch.trim().toLowerCase();

      if (!search) {
        return this.dealers;
      }

      return this.dealers.filter((dealer) =>
        (dealer.businessName || '').toLowerCase().includes(search) ||
        (dealer.ownerName || '').toLowerCase().includes(search) ||
        (dealer.mobile || '').includes(search)
      );
    },
  },

  mounted() {
    this.fetchDealers();
  },

  activated() {
    if (this.hasLoaded) {
      this.fetchDealers();
    }
  },

  methods: {
    async fetchDealers() {
      try {
        this.loading = true;
        this.dealers = [];

        const response = await fetchTempDealerList(this.profileId, this.token);

        if (response.data.status === "success") {
          this.dealers = (response.data.data || []).map((dealer) => ({
            id: dealer.id,
            businessName: dealer.business_name,
            ownerName: dealer.owner_name,
            mobile: dealer.mobile,
            distributorName: dealer.distributor_name,
            createdBy: dealer.created_by_name,
            createdAt: dealer.created_at,
          }));
        } else {
          console.log(response.data.message);
        }
      } catch (e) {
        console.error("Failed to fetch pending dealers.", e);
      } finally {
        this.loading = false;
        this.hasLoaded = true;
      }
    },

    // Format as "YYYY-MM-DD | HH:MM:SS AM/PM"
    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";

      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "Invalid Date";

      const formattedDate = date
        .toLocaleDateString("en-CA", { year: "numeric", month: "2-digit", day: "2-digit" })
        .replace(/\//g, "-");
      const formattedTime = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      return `${formattedDate} | ${formattedTime}`;
    },

    reviewDealer(id) {
        this.$router.push({ path: `/review-dealer/${id}` });
    },
  },
};
</script>

<style scoped>
.container {
  background-color: #ffffff;
  box-shadow: none;
  border-radius: 8px;
  padding: 10px;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  box-sizing: border-box;
}

.filter-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
  align-items: flex-end;
  margin-bottom: 10px;
}

.filter-item {
  display: flex;
  flex-direction: column;
  min-width: fit-content;
}

.dealer-name-item {
  width: 340px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}

input[type="text"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
  box-sizing: border-box;
}

input[type="text"]:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  margin: 10px 0;
  border: none;
}

th {
  text-align: left;
  border-bottom: 2px solid #ddd;
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
}

td {
  text-align: left;
  border: none;
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eee;
}

tbody tr:last-child td {
  border-bottom: none;
}

.dealer-actions {
  text-align: center;
}

.icon-button {
  margin: 0;
  font-size: 15px;
  background-color: transparent;
  border: none;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

.not-found-widget {
  max-width: 500px;
  text-align: center;
}

.not-found-widget img {
  width: 250px;
}

.not-found-widget p {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -25px;
}

@media (max-width: 768px) {
  .dealer-name-item {
    width: 100%;
  }
}
</style>